import moment from 'moment';
import { SubmissionError, change as changeFieldValue } from 'redux-form';
import { get, omit, isEmpty, noop, pick } from 'lodash';

import requestsStatuses from 'modules/common/constants/requestsStatuses';
import createAsyncAction from 'shared/utils/createAsyncAction';
import { apiErrorHandler } from 'shared/utils/apiErrorHandler';
import sleep from 'shared/utils/sleep';
import diff from 'shared/utils/object-diff';
import { addToast } from 'modules/messageSystem/actions/addToast';
import { replaceTo } from 'modules/auth/actions/redirectAction';
import { updateUser } from 'modules/user/actions/_api/updateUser';
import { createUser } from 'modules/user/actions/_api/createUser';
import { formatPhoneNumber, parsePhoneNumber } from 'modules/user/utils/formatPhoneNumber';
import { getCommonUserConfig } from 'modules/user/selectors/users';
import { PATIENT } from 'modules/user/constants/userTypes';
import {
  USER_SECONDARY_KEYS_DUPLICATION_ERROR,
  apiErrorMessagesMap as usersApiErrorMessagesMap,
} from 'modules/user/constants/apiErrorCodes';
import { isValidSpecialtyGroupProvider } from 'modules/appointmentsManagement/utils/appontmentFormUtils';
import {
  PAST_SCHEDULE_START_TIME,
  FLEXIBLE_SLOT_IS_TAKEN,
  CANNOT_CREATE_GENERAL_ERROR,
  apiErrorMessagesMap,
  modalErrors,
} from '../constants/apiErrorCodes';
import { FLEXIBLE } from '../constants/schedulingTypes';
import { CREATE_APPOINTMENT_FORM } from '../constants/formTypes';
import { getSelectedContract } from '../selectors/contracts';
import { createAppointment } from './_api/createAppointment';
import { createMessagingAppointment } from './_api/createMessagingAppointment';
import { getPatientByExternalId } from './_api/getPatientByExternalId';
import { invokeAppointmentErrorModal } from './invokeModals';
import createAction from './index';

export const SUBMIT_CREATE_APPOINTMENT_FORM = createAsyncAction(createAction('SUBMIT_CREATE_APPOINTMENT_FORM'));
export const SUBMIT_ADD_TO_GROUP_APPOINTMENT_FORM = createAsyncAction(createAction('SUBMIT_ADD_TO_GROUP_APPOINTMENT_FORM'));
export const RESET_APPOINTMENT_CONFIG = createAction('RESET_APPOINTMENT_CONFIG');
export const CLEAR_OPERATION_HOURS = createAction('CLEAR_OPERATION_HOURS');
export const RESET_TIME_SLOTS = createAction('RESET_TIME_SLOTS');
export const RESET_CALENDAR_SLOTS = createAction('RESET_CALENDAR_SLOTS');
export const CLEAR_APPOINTMENT_SERVICE_PROVIDERS = createAction('CLEAR_APPOINTMENT_SERVICE_PROVIDERS');

export const resetAppointmentConfig = () => (dispatch) => dispatch({
  type: RESET_APPOINTMENT_CONFIG,
});

export const clearOperationHours = () => dispatch => dispatch({
  type: CLEAR_OPERATION_HOURS,
});

export const clearTimeSlots = () => dispatch => dispatch({
  type: RESET_TIME_SLOTS,
});

export const clearCalendarTimeSlots = () => ({
  type: RESET_CALENDAR_SLOTS,
});

export const clearAppointmentServiceProviders = () => ({
  type: CLEAR_APPOINTMENT_SERVICE_PROVIDERS,
});

const mapErrors = (errors, field) => {
  const mappedReduxErrors = Object.entries(errors).reduce((acc, [key, value]) => ({ ...acc, [`${ field }.${ key }`]: value }), {});

  Object.entries(mappedReduxErrors).forEach(([key, value]) => {
    const nested = key.split('.');

    if (nested.length) {
      let obj = mappedReduxErrors;

      nested.forEach((item, index) => {
        const passedValue = index < nested.length - 1 ? {} : value;
        const hasArray = item.split('[');

        if (hasArray[1]) {
          const itemKey = hasArray[0];
          const itemIndex = +hasArray[1].substring(0, hasArray[1].length - 1);
          obj[itemKey] = [];
          obj[itemKey][itemIndex] = passedValue;
          obj = obj[itemKey][itemIndex];
        } else {
          obj[item] = isEmpty(passedValue) ? obj[item] || passedValue : passedValue;
          obj = obj[item];
        }
      });
    }
  });
  return mappedReduxErrors;
};

const trimUserFields = obj => {
  const data = { ...obj };

  Object.entries(data).forEach(([key, value]) => {
    if (value && typeof value === 'string') data[key] = value.trim();
  });

  return data;
};

const defaultUsersModel = { firstName: '', lastName: '', email: null, dateOfBirth: '', gender: 'UNKNOWN', phoneNumber: '' };

export const checkAndCreateOrUpdateUser = (user, field, index, organizationId, reduxAction, form, userErrorCallback = noop) => async (dispatch, getState) => {
  const data = trimUserFields(user);
  const userConfig = getCommonUserConfig(getState());
  let action = 'CREATE';
  data.externalId = data.externalId ? data.externalId.trim() : null;

  const errorHandler = async (error) => {
    const usersApiErrorMessageMapMod = { ...usersApiErrorMessagesMap };
    if (userConfig.requiredFields.length) {
      usersApiErrorMessageMapMod[USER_SECONDARY_KEYS_DUPLICATION_ERROR].fields = userConfig.secondaryFields;
      usersApiErrorMessageMapMod[USER_SECONDARY_KEYS_DUPLICATION_ERROR].step = field === 'customer' ? 2 : 3;
    }

    dispatch({
      type: reduxAction.failure,
      meta: { errors: apiErrorHandler(error, false, usersApiErrorMessageMapMod, true) },
    });

    await sleep(500);

    const errors = apiErrorHandler(error, true, usersApiErrorMessageMapMod, false, userErrorCallback);
    const generalError = errors && errors['_error'];

    throw new SubmissionError(generalError ? errors : mapErrors(errors, field));
  };

  if (!data.userId && userConfig.secondaryFields.length) {
    if (data.dateOfBirth) data.dateOfBirth = moment.utc(data.dateOfBirth, 'D/M/YYYY', true).valueOf();
    data.phoneNumber = formatPhoneNumber(data.phoneNumber);
    data.organizationId = organizationId;
    data.type = PATIENT;

    try {
      const secondaryMatchFound = await dispatch(getPatientByExternalId({ ...pick(data, userConfig.requiredFields), orgId: organizationId.toString() }));

      if (secondaryMatchFound) {
        dispatch(changeFieldValue(form, field, {
          ...data,
          initUser: secondaryMatchFound.payload,
          checkStatus: requestsStatuses.success,
        }));
      }
    } catch (error) {
      console.warn(error);
    }
  }

  if (!data.userId) {
    if (data.dateOfBirth) data.dateOfBirth = moment.utc(data.dateOfBirth, 'D/M/YYYY', true).valueOf();
    data.phoneNumber = formatPhoneNumber(data.phoneNumber);
    data.organizationId = organizationId;
    data.type = PATIENT;

    try {
      const { payload: { user } } = await dispatch(createUser(data));
      data.userId = user.userId;

      dispatch(changeFieldValue(CREATE_APPOINTMENT_FORM, field, {
        ...omit(data, ['organizationId']),
        externalId: user.externalId.includes('***') ? data.externalId : user.externalId,
        orgId: user.orgId,
        initUser: {
          ...omit(user, ['profile']),
          // TODO: Apply permissions once patient creation flow would be needed
          permissions: undefined,
          isRestrictedUser: false,
        },
        checkStatus: requestsStatuses.success,
        userId: user.userId,
        phoneNumber: parsePhoneNumber(user.phoneNumber),
        dateOfBirth: user.dateOfBirth ? moment(+user.dateOfBirth).format('DD/MM/YYYY') : null,
      }));

      dispatch(addToast('success', 'appt-create-customer-create-success', {
        id: 'AdminConsole.Appointments.CreateForm.NewPatient.Snackbar.Success',
        defaultMessage: '** Patient created successfully',
      }));
    } catch (error) {
      await errorHandler(error);
    }
  } else {
    action = 'UPDATE';
    const fetchedUserData = user.initUser;
    if (fetchedUserData.isRestrictedUser) return { data, action };

    const currentUserData = omit({ ...defaultUsersModel, ...data },
      'checkStatus',
      'initUser',
      'lastSearchValue',
    );
    if (currentUserData.dateOfBirth) currentUserData.dateOfBirth = moment.utc(currentUserData.dateOfBirth, 'D/M/YYYY', true).valueOf().toString();
    currentUserData.phoneNumber = formatPhoneNumber(currentUserData.phoneNumber);

    if (!currentUserData.email) currentUserData.email = '';

    const updatedFields = diff(fetchedUserData, currentUserData);

    if (!isEmpty(updatedFields)) {
      const userData = { ...fetchedUserData, ...updatedFields };

      try {
        await dispatch(updateUser(userData))
          .then(({ user }) => {
            dispatch(changeFieldValue(form, field, {
              ...data,
              initUser: {
                ...omit(user, 'profile'),
                ...user.profile,
                permissions: fetchedUserData.permissions,
              },
            }));
          });

        if (field === 'customer') {
          dispatch(
            addToast('success', 'appt-create-customer-update-success', {
              id: 'AdminConsole.Appointments.Create.Dialog.EditCustomer.SuccessMessage',
              defaultMessage: '** Customer details updated successfully',
            }));
        } else {
          dispatch(
            addToast('success', 'appt-create-patient-update-success', {
              id: 'AdminConsole.Appointments.Create.Dialog.EditPatient.SuccessMessage',
              defaultMessage: '** Patient details updated successfully',
            }));
        }
      } catch (error) {
        await errorHandler(error);
      }
    }
  }

  return { data, action };
};

export const submitCreateAppointmentForm = (formData, isExternalInit, setStep, scrollToError) => async (dispatch, getState) => {
  const data = { ...formData };
  const state = getState();
  const selectedContract = getSelectedContract(state, data.contractId);
  const fixedSlot = !!get(selectedContract, 'service.config.fixedAppointmentDuration');
  const flexibleSlot = get(selectedContract, 'service.config.schedulingType') === FLEXIBLE;

  dispatch({ type: SUBMIT_CREATE_APPOINTMENT_FORM.pending });

  data.allowedConversationTypes = [data.conversationType];

  if (data.timeSlot) {
    const slots = data.timeSlot.split('-');
    data.schedule = {
      startTimestamp: Number(slots[0]),
      endTimestamp: Number(slots[1]),
    };
  } else if (data.flexible) {
    data.schedule = { startTimestamp: data.flexible.from };
    if (!fixedSlot) data.schedule.endTimestamp = data.flexible.to;
  }

  const userErrorCallback = (errors) => {
    const knownApiError = usersApiErrorMessagesMap[Object.entries(errors)[0][1]];
    if (knownApiError) {
      return setStep(knownApiError.step, () => { scrollToError(errors); });
    }
    scrollToError(errors);
  };

  const { data: customerData, action: customerAction } = await dispatch(checkAndCreateOrUpdateUser(
    data.customer,
    'customer',
    null,
    formData.organizationId,
    SUBMIT_CREATE_APPOINTMENT_FORM,
    CREATE_APPOINTMENT_FORM,
    userErrorCallback,
  ));

  if (customerAction === 'CREATE') data.customer.initUser = customerData;

  const subjects = [];
  for (let i = 0; i < formData.patient.length; i += 1) {
    const patient = { ...data.patient[i] };

    if (patient.initUser === 'customer') {
      subjects.push(customerData);
    } else {
      const { data: patientData, action: patientAction } = await dispatch(checkAndCreateOrUpdateUser( // eslint-disable-line
        patient,
        `patient[${ i }]`,
        i,
        formData.organizationId,
        SUBMIT_CREATE_APPOINTMENT_FORM,
        CREATE_APPOINTMENT_FORM,
        userErrorCallback,
      ));
      subjects.push(patientData);

      if (patientAction === 'CREATE') data.patient[i].initUser = patientData;
    }
  }

  data.subject = { userId: subjects[subjects.length - 1].userId };
  data.participants = [{
    userId: customerData.userId,
    participantType: 'CUSTOMER',
  }];

  if (isValidSpecialtyGroupProvider(data.providerUserId)) {
    data.providerGroupSpecialtyId = data.providerUserId;
    delete data.providerUserId;
  } else if (data.providerUserId && data.providerUserId !== 'PROVIDERS_GROUP') {
    data.participants.push({
      userId: data.providerUserId,
      participantType: 'PROVIDER',
    });
  }

  const requestData = omit(data, [
    'subjects',
    'patient',
    'customer',
    'organizationId',
    'timeSlot',
    '_hackValidation',
    'date',
    'endDate',
    'flexible',
    'timeSlot',
    'conversationType',
    'providerUserId',
    'appointmentsInGroup',
    'customerIsPatientIndex',
    'appointmentIndex',
    'totalAppointments',
    'consultationType',
    'initialMessage',
    'attachments',
  ]);

  return dispatch(createAppointment(requestData))
    .then(response => {

      dispatch({ type: SUBMIT_CREATE_APPOINTMENT_FORM.success });

      const appointmentId = data.groupId
        ? get(data, 'appointmentsInGroup[0].appointmentId')
        : get(response, 'payload.appointmentId');

      console.log('apptId', appointmentId);

      if (isExternalInit) {
        replaceTo('/appointment/create/success');
      } else {
        replaceTo('/appointments?list=true');
      }

      if (isExternalInit) return;

      data.groupId
        ? dispatch(
          addToast('success', 'appt-create-group-success', {
            id: 'AdminConsole.Appointments.CreateForm.GroupOfAppts.Snackbar.Success',
            defaultMessage: '** Group of appointments created successfully',
          }))
        : dispatch(
          addToast('success', 'appt-create-success', {
            id: 'Patient.Appointment.Create.SuccessMessage.Snackbar',
            defaultMessage: '** Appointment created successfully',
          }));
    })
    .catch(error => {
      const apiErrorMessagesMapCopy = { ...apiErrorMessagesMap };

      dispatch({
        type: SUBMIT_CREATE_APPOINTMENT_FORM.failure,
        meta: { errors: apiErrorHandler(error, false, apiErrorMessagesMapCopy, true) },
      });

      if (flexibleSlot) {
        apiErrorMessagesMapCopy[PAST_SCHEDULE_START_TIME] = apiErrorMessagesMapCopy[FLEXIBLE_SLOT_IS_TAKEN];
      }

      const errors = apiErrorHandler(error, true, apiErrorMessagesMapCopy, false);

      if (Object.values(errors).some(value => modalErrors.includes(+value))) {
        const errorCode = Object.values(errors).find(value => modalErrors.includes(+value));

        return dispatch(invokeAppointmentErrorModal(apiErrorMessagesMapCopy[errorCode]));
      }

      throw new SubmissionError(apiErrorHandler(error, true, apiErrorMessagesMapCopy, false, (errors) => {
        const knownApiError = apiErrorMessagesMap[Object.entries(errors)[0][1]];
        if (knownApiError) {
          return setStep(knownApiError.step, () => { scrollToError(errors); });
        }
        scrollToError(errors);
      }));
    });
};

export const submitCreateMessagingAppointmentForm = (formData, isExternalInit) => (dispatch) => {
  dispatch({ type: SUBMIT_CREATE_APPOINTMENT_FORM.pending });

  const data = { ...formData };

  data.subject = { userId: data.customer.userId };
  data.participants = [{
    userId: data.customer.userId,
    participantType: 'CUSTOMER',
  }];

  if (data.providerUserId && data.providerUserId !== 'PROVIDERS_GROUP') {
    data.participants.push({
      userId: data.providerUserId,
      participantType: 'PROVIDER',
    });
  }

  data.initialMessages = [
    { text: data.initialMessage },
    ...data.attachments.map(({ text, attachment }) => ({ text, attachment })),
  ];

  const requestData = omit(data, [
    'subjects',
    'patient',
    'customer',
    'organizationId',
    'timeSlot',
    '_hackValidation',
    'date',
    'endDate',
    'flexible',
    'timeSlot',
    'conversationType',
    'providerUserId',
    'appointmentsInGroup',
    'customerIsPatientIndex',
    'appointmentIndex',
    'totalAppointments',
    'consultationType',
    'initialMessage',
    'attachments',
  ]);

  return dispatch(createMessagingAppointment(requestData))
    .then(response => {

      dispatch({ type: SUBMIT_CREATE_APPOINTMENT_FORM.success });

      const appointmentId = get(response, 'payload.appointmentId');

      console.log('apptId', appointmentId);

      if (isExternalInit) {
        return replaceTo(`/appointment/create/success-messaging/${ appointmentId }`);
      }

      replaceTo('/appointments?list=true');

      dispatch(
        addToast('success', 'appt-create-success', {
          id: 'Patient.Appointment.Create.SuccessMessage.Snackbar',
          defaultMessage: '** Appointment created successfully',
        }));
    })
    .catch(error => {
      console.warn(error);
      const apiErrorMessagesMapCopy = { ...apiErrorMessagesMap };

      dispatch({
        type: SUBMIT_CREATE_APPOINTMENT_FORM.failure,
        meta: { errors: apiErrorHandler(error, false, apiErrorMessagesMapCopy, true) },
      });

      const errors = apiErrorHandler(error, true, apiErrorMessagesMapCopy, false);

      if (Object.values(errors).some(value => modalErrors.includes(+value))) {
        const errorCode = Object.values(errors).find(value => modalErrors.includes(+value));

        return dispatch(invokeAppointmentErrorModal(apiErrorMessagesMapCopy[errorCode]));
      }

      return dispatch(invokeAppointmentErrorModal(apiErrorMessagesMapCopy[CANNOT_CREATE_GENERAL_ERROR], () => {
        isExternalInit
          ? replaceTo('/appointment/create/cancel')
          : replaceTo('/appointments?list=true');
      }));
    });
};
